import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import colors from '../theme/Colors';
import CustomSelect from './CustomSelect';

const headerCellSx = {
  border: '1px solid #dee2e6',
  backgroundColor: '#f8f9fa',
  fontWeight: 700,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  py: 1.25,
  px: 1,
};

const bodyCellSx = {
  border: '1px solid #dee2e6',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  py: 1,
  px: 1,
};

const infoBoxSx = {
  border: '1px solid #dee2e6',
  p: 1.5,
  minWidth: 240,
};

const getQuantityForSize = (sizes = [], targetSize) => {
  const sizeObj = sizes.find((size) => size.size === targetSize);
  return sizeObj ? sizeObj.quantity : 0;
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
};

const OrderShareDialog = ({
  open,
  handleClose,
  selectedCustomer,
  setSelectedCustomer,
  customers,
  order,
  previewRef,
  handleShare
}) => {
  const orderItems = order?.items || [];
  const subtotal = Number(order?.total_price || 0);
  const discount = Number(order?.discount || 0);
  const total = subtotal - discount;

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        setSelectedCustomer('');
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle>Share Order</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <CustomSelect
            label="Select Customer"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            options={customers?.map((c) => ({ id: c._id, name: c.name }))}
            sx={{ minWidth: 250 }}
          />
          {order && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Preview:
              </Typography>
              <Box
                sx={{
                  overflow: 'auto',
                  maxHeight: '60vh',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  backgroundColor: '#fff',
                  p: 2
                }}
              >
                <Box
                  ref={previewRef}
                  sx={{
                    minWidth: 1180,
                    width: 'max-content',
                    backgroundColor: '#fff',
                    color: '#111',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={infoBoxSx}>
                      <Typography><strong>CUSTOMER:</strong> {order?.customer?.name || 'N/A'}</Typography>
                      <Typography><strong>ORDER NO:</strong> {order?.orderId || 'N/A'}</Typography>
                      <Typography><strong>STATUS:</strong> {order?.status || 'N/A'}</Typography>
                    </Box>
                    <Box sx={infoBoxSx}>
                      <Typography><strong>DATE:</strong> {formatDate(order?.createdAt)}</Typography>
                      <Typography><strong>ORDER BY:</strong> {order?.created_by?.name || 'N/A'}</Typography>
                      <Typography><strong>SHIPPED VIA:</strong> {order?.shipped_via || 'N/A'}</Typography>
                      {order?.status === 'SHIPPED' && (
                        <Typography><strong>SHIPPED DATE:</strong> {formatDate(order?.shipped_date)}</Typography>
                      )}
                    </Box>
                  </Box>

                  <Paper sx={{ border: '1px solid #dee2e6', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx} rowSpan={2}>PARTY</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>P-STYLE</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>ITEM</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>COLOR</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>XS</TableCell>
                          <TableCell sx={headerCellSx} colSpan={2}>SMALL</TableCell>
                          <TableCell sx={headerCellSx} colSpan={2}>MEDIUM</TableCell>
                          <TableCell sx={headerCellSx} colSpan={2}>LARGE</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>XL</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>2XL</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>3XL</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>MIX</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>TOT QTY</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>RATE</TableCell>
                          <TableCell sx={headerCellSx} rowSpan={2}>AMOUNT</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={headerCellSx}>6M</TableCell>
                          <TableCell sx={headerCellSx}>2Y</TableCell>
                          <TableCell sx={headerCellSx}>12M</TableCell>
                          <TableCell sx={headerCellSx}>4Y</TableCell>
                          <TableCell sx={headerCellSx}>12M</TableCell>
                          <TableCell sx={headerCellSx}>6Y</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderItems.map((item, index) => {
                          const totalQty = (item?.sizes || []).reduce(
                            (sum, size) => sum + Number(size.quantity || 0),
                            0
                          );
                          const amount = Number(item?.price || 0) * totalQty;

                          return (
                            <TableRow key={index}>
                              <TableCell sx={bodyCellSx}>{item?.stock?.party?.name || '-'}</TableCell>
                              <TableCell sx={bodyCellSx}>{item?.stock?.p_style?.name || '-'}</TableCell>
                              <TableCell sx={bodyCellSx}>{item?.stock?.item?.name || '-'}</TableCell>
                              <TableCell sx={bodyCellSx}>{item?.stock?.color || '-'}</TableCell>
                              <TableCell sx={bodyCellSx}>{getQuantityForSize(item?.sizes, 'XS')}</TableCell>
                              <TableCell sx={bodyCellSx} colSpan={2}>{getQuantityForSize(item?.sizes, 'S')}</TableCell>
                              <TableCell sx={bodyCellSx} colSpan={2}>{getQuantityForSize(item?.sizes, 'M')}</TableCell>
                              <TableCell sx={bodyCellSx} colSpan={2}>{getQuantityForSize(item?.sizes, 'L')}</TableCell>
                              <TableCell sx={bodyCellSx}>{getQuantityForSize(item?.sizes, 'XL')}</TableCell>
                              <TableCell sx={bodyCellSx}>{getQuantityForSize(item?.sizes, '2XL')}</TableCell>
                              <TableCell sx={bodyCellSx}>{getQuantityForSize(item?.sizes, '3XL')}</TableCell>
                              <TableCell sx={bodyCellSx}>{getQuantityForSize(item?.sizes, 'MIX')}</TableCell>
                              <TableCell sx={bodyCellSx}>{totalQty}</TableCell>
                              <TableCell sx={bodyCellSx}>{item?.price || 0}</TableCell>
                              <TableCell sx={bodyCellSx}>{amount}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Box sx={{ ...infoBoxSx, minWidth: 260 }}>
                      <Typography><strong>SUB TOTAL:</strong> {subtotal.toFixed(2)}</Typography>
                      <Typography><strong>DISCOUNT:</strong> {discount}</Typography>
                      <Typography><strong>TOTAL:</strong> {total.toFixed(2)}</Typography>
                      <Typography><strong>GST %:</strong> {order?.gst || 0}</Typography>
                      <Typography><strong>QST %:</strong> {order?.qst || 0}</Typography>
                      <Typography><strong>G TOTAL:</strong> {Number(order?.final_price || 0).toFixed(2)}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            handleClose();
            setSelectedCustomer('');
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          disabled={!selectedCustomer}
          sx={{
            backgroundColor: colors.primary,
            color: 'black',
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9
            }
          }}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderShareDialog;
